import type { ReactNode } from 'react'

import type { ColorScheme, SelectOption } from '@hivewatch/dark'
import { Select } from '@hivewatch/dark'

import { FilterOptionI } from '@/utils/filterDrawers'

export interface SelectFilterRowIProps<T> {
  label: string
  placeholder: string
  colorScheme: ColorScheme
  options: FilterOptionI<T>[]
  value: FilterOptionI<T> | null | undefined
  onChange: (value: FilterOptionI<T> | null) => void
  /**
   * Whether the field shows a per-field clear affordance. Defaults to `true`
   * so a single-select can be cleared on its own (parity with the multi-select
   * rows' chip clear) without wiping the other filters via Clear All. The dark
   * Select defaults this to `false`, so it must be set here.
   */
  isClearable?: boolean
  /**
   * Stable string key per option. Defaults to `String(option.value)`; rows
   * whose values are objects (e.g. a `{ gte }` date preset) must key by label
   * instead, since every object stringifies to `[object Object]`.
   */
  getOptionKey?: (option: FilterOptionI<T>) => string
  /** Set false for a plain picker with no type-to-filter input. */
  isSearchable?: boolean
  /** Server-side option loading (e.g. Facility): fired as the user types. */
  onInputChange?: (value: string) => void
  isLoading?: boolean
  noOptionsMessage?: (obj: { inputValue: string }) => string | null
  /**
   * Overrides how an option renders (e.g. a facility's subtitle + "matched
   * by address" cue). Passed straight through to the dark Select; the
   * option object it receives carries whatever extra fields the caller's
   * options have (see the `selectOptions` mapping below), not just
   * `{ value, label }`.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formatOptionLabel?: (
    option: any,
    formatOptionLabelMeta: { context: 'menu' | 'value'; inputValue: string }
  ) => ReactNode
  /**
   * Overrides react-select's default label-only client-side filter. Required
   * for any onInputChange-driven server search (e.g. Facility): the options
   * array already IS the server's filtered result set, but react-select
   * still re-filters it against the typed text by label before rendering —
   * an address-only match's label doesn't contain what was typed, so without
   * this it silently disappears from the menu despite being a valid result.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filterOption?: (option: any, rawInput: string) => boolean
  'data-testid'?: string
}

/**
 * Single-select filter row on the dark Select. Options are keyed by
 * `String(value)` by default (safe for boolean and enum values); pass
 * `getOptionKey` for object-valued options. Shared across the dark page filter
 * drawers — see `./FilterDrawer`.
 */
export const SelectFilterRow = <T,>({
  label,
  placeholder,
  colorScheme,
  options,
  value,
  onChange,
  isClearable = true,
  getOptionKey,
  isSearchable,
  onInputChange,
  isLoading,
  noOptionsMessage,
  formatOptionLabel,
  filterOption,
  'data-testid': dataTestId,
}: SelectFilterRowIProps<T>) => {
  const toKey =
    getOptionKey ?? ((option: FilterOptionI<T>) => String(option.value))

  // Spread the original option (not just { value, label }) so a
  // formatOptionLabel renderer can read extra fields — a facility's
  // subtitle/matchedField, say.
  const selectOptions: SelectOption[] = options.map((option) => ({
    ...option,
    value: toKey(option),
    label: option.label,
  }))

  // Fall back to the value's own label when it isn't in the current options —
  // otherwise a selection backed by async options (e.g. a server-searched
  // Facility) renders blank until the options load. The stored value already
  // carries its label, so there's no need to wait for the options list.
  const selectedOption =
    value != null
      ? selectOptions.find((option) => option.value === toKey(value)) ?? {
          ...value,
          value: toKey(value),
          label: value.label,
        }
      : null

  const handleChange = (option: SelectOption | null) => {
    if (option === null) {
      onChange(null)
      return
    }
    onChange(options.find((o) => toKey(o) === option.value) ?? null)
  }

  return (
    <Select
      colorScheme={colorScheme}
      data-testid={dataTestId}
      filterOption={filterOption}
      formatOptionLabel={formatOptionLabel}
      isClearable={isClearable}
      isLoading={isLoading}
      isSearchable={isSearchable}
      label={label}
      noOptionsMessage={noOptionsMessage}
      onChange={handleChange}
      onInputChange={onInputChange}
      options={selectOptions}
      placeholder={placeholder}
      value={selectedOption}
    />
  )
}
